import { Box } from '@mantine/core';
import { spotlight } from '@mantine/spotlight';
import {
	FILTER_OPTIONS_DIVIDER,
	FilterOptionType,
} from '@repo/common/components/Filter';
import { EntityType } from '@repo/common/enums/entityType';
import { useKeyPress } from 'ahooks';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { IQuestion } from '../../api';
import type { FetchModelInfiniteListHook } from '../../api/factories/types';
import { useCatalogInfiniteList } from '../../api/hooks/resourceCatalog';
import { useCatalogFilter } from '../../components/Filter/useCatalogFilter';
import { TableV2 } from '../../components/TableV2';
import type { OnCellClickHandlerParams } from '../../components/TableV2/types';
import { GLOBAL_SHORTCUTS } from '../../constants/shortcuts';
import { useActions, useColumns } from './TeamQuestionsPage.hooks';

const DEFAULT_SORT = {
	field: 'created_at',
	order: 'desc',
} as const;

const COLUMN_VISIBILITY = {
	catalogType: EntityType.question,
	catalogServerType: EntityType.question,
} as const;

const QUICK_ACTIONS = [
	'actions::assignee',
	'actions::assignee_group',
	'actions::priority',
	'actions::status',
	'actions::sidebar',
] as const;

const PAGINATION_LIST_OPTIONS = {
	suspense: true,
};

const WITH_CUSTOM_PROPERTIES = [EntityType.question];

const ADDITIONAL_FILTER_OPTIONS = [
	FilterOptionType.OWNERS,
	FilterOptionType.CREATED_TIME,
];

const IGNORE_FILTERS_FROM_CATALOG = [
	FilterOptionType.IS_TESTED,
	FilterOptionType.TESTS_PASSING,
	FilterOptionType.IS_MONITORED,
	FilterOptionType.MONITORS_PASSING,
	FilterOptionType.LINEAGE,
];

export function TeamQuestionsTable() {
	const navigate = useNavigate();
	const { teamId } = useParams();

	const columns = useColumns();
	const actions = useActions();

	useKeyPress(GLOBAL_SHORTCUTS.SPOTLIGHT_PALETTE, () => {
		spotlight.open();
	});

	const handleCellClick = useCallback(
		({ column, record }: OnCellClickHandlerParams<IQuestion>) => {
			if (column.navigate !== false) {
				navigate(`/questions/${record.id}`);
			}
		},
		[navigate]
	);

	const catalogFilterOptions = useCatalogFilter();

	const filterOptions = useMemo(
		() => [
			...catalogFilterOptions
				.filter(
					(option) =>
						option === FILTER_OPTIONS_DIVIDER ||
						!IGNORE_FILTERS_FROM_CATALOG.includes(option.type)
				)
				.map((option) => {
					if (
						option === FILTER_OPTIONS_DIVIDER ||
						option.type !== FilterOptionType.OWNERS
					) {
						return option;
					}

					return {
						...option,
						label: 'Asked by',
					};
				}),
		],
		[catalogFilterOptions]
	);

	const defaultRequiredSearchParams = useMemo(
		() => ({
			team_id: teamId,
			entity_type: EntityType.question,
		}),
		[teamId]
	);

	return (
		<Box pb="xl">
			<TableV2<IQuestion>
				withCustomProperties={WITH_CUSTOM_PROPERTIES}
				pluralTypeString="questions"
				withCsvExport
				withServerCsvExport
				withInfiniteScroll
				withCheckbox
				withInteractiveHeader
				onCellClick={handleCellClick}
				withSearch
				withFilterOptions={filterOptions}
				columns={columns}
				withActions={actions}
				withQuickActions={QUICK_ACTIONS}
				usePaginationList={
					useCatalogInfiniteList as FetchModelInfiniteListHook<IQuestion>
				}
				additionalFilters={ADDITIONAL_FILTER_OPTIONS}
				columnVisibility={COLUMN_VISIBILITY}
				defaultRequiredSearchParams={defaultRequiredSearchParams}
				defaultSort={DEFAULT_SORT}
				usePaginationListOptions={PAGINATION_LIST_OPTIONS}
			/>
			<Box h="xl" />
		</Box>
	);
}
